import { resolveContentLink } from "@/lib/contentLink";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Calendar, ArrowRight, Map, TrendingDown, Users, AlertTriangle, CheckCircle2, Sparkles } from "lucide-react";
import { EditableImage } from "@/components/editable/EditableImage";
import carrierepadenHero from "@/assets/images/carrierepaden-hero-photo.webp";
import { ClientLogos } from "@/components/ClientLogos";
import { ScheduleDemo } from "@/components/sections";
import { PartnerForChange } from "@/components/sections/PartnerForChange";
import { PageContainer } from "@/components/layouts/MainLayout";
import { T, THtml } from "@/components/editable/T";
import { getLocalizedRoutePath } from "@/lib/routeUtils";
import { FeatureImageFrame } from "@/components/FeatureImageFrame";
import jobMatrixNl from "@/assets/illustrations/carrierepaden-jobmatrix-nl.webp";
import jobMatrixEn from "@/assets/illustrations/carrierepaden-jobmatrix-en.webp";
import jobMatrixDe from "@/assets/illustrations/carrierepaden-jobmatrix-de.webp";
import padPlanNl from "@/assets/illustrations/carrierepaden-padnaarplan-nl.webp";
import padPlanEn from "@/assets/illustrations/carrierepaden-padnaarplan-en.webp";
import padPlanDe from "@/assets/illustrations/carrierepaden-padnaarplan-de.webp";
import skillgapNl from "@/assets/illustrations/carrierepaden-skillgap-nl.webp";
import skillgapEn from "@/assets/illustrations/carrierepaden-skillgap-en.webp";
import skillgapDe from "@/assets/illustrations/carrierepaden-skillgap-de.webp";

const P = "careerPaths";

const featureColors = [
  "#F3EDFD", "#FDF2ED", "#FFFBEB"];


const Carrierepaden = () => {
  const { i18n } = useTranslation();
  const lang = i18n.language || 'nl';
  const demoPath = getLocalizedRoutePath('demo', lang);
  const jobMatrixImage = lang.startsWith('de') ? jobMatrixDe : lang.startsWith('en') ? jobMatrixEn : jobMatrixNl;
  const padPlanImage = lang.startsWith('de') ? padPlanDe : lang.startsWith('en') ? padPlanEn : padPlanNl;
  const skillgapImage = lang.startsWith('de') ? skillgapDe : lang.startsWith('en') ? skillgapEn : skillgapNl;

  const features = [
  {
    titleKey: "features.row1.title",
    titleDefault: "Carrièrepaden zichtbaar voor iedereen",
    bodyKey: "features.row1.body",
    bodyDefault: "In de job matrix zie je in één oogopslag welke rollen bereikbaar zijn, zowel een stap omhoog als een stap opzij. Geen giswerk meer over wat de volgende stap kan zijn, voor niemand.",
    imageKey: 'row1-image',
    color: featureColors[0],
    image: jobMatrixImage,
    imageAlt: "Functiematrix met functiefamilies, niveaus en salarisbandbreedtes",
    noFrame: true,
    cta: { labelKey: "features.row1.cta", labelDefault: "Ontdek functiehuis en functieprofielen", link: resolveContentLink(`/${lang}/platform/work-design/functiehuis-functieprofielen/`, lang) }
  },
  {
    titleKey: "features.row2.title",
    titleDefault: "De skillgap per rol, concreet gemaakt",
    bodyKey: "features.row2.body",
    bodyDefault: "Per rol is zichtbaar welke skills er nog ontbreken, zodat het gesprek over groei over concrete stappen gaat.",
    imageKey: 'row2-image',
    color: featureColors[1],
    image: skillgapImage,
    imageAlt: "Functieprofiel projectmanager met scores op kernwaarden",
    noFrame: true,
    demoCta: true
  },
  {
    titleKey: "features.row3.title",
    titleDefault: "Van pad naar plan",
    bodyKey: "features.row3.body",
    bodyDefault: "Wat uit het carrièrepad komt, landt direct in een ontwikkelplan met opvolging in de 1:1's.",
    imageKey: 'row3-image',
    color: featureColors[2],
    image: padPlanImage,
    imageAlt: "Carrièrepad met opeenvolgende rollen van HR advisor naar HR manager",
    noFrame: true,
    cta: { labelKey: "features.row3.cta", labelDefault: "Ontdek ontwikkelplannen", link: resolveContentLink(`/${lang}/platform/performance-management/ontwikkelplannen/`, lang) }
  }];


  return (
    <>
      {/* Hero Section */}
      <section className="pt-[140px] pb-6 px-6 relative">
        <PageContainer className="relative z-10">
          <div className="flex items-center gap-2 text-sm text-muted-foreground mb-6">
            <Link to={`/${lang}/`} className="hover:text-primary transition-colors">
              <T page={P} k="breadcrumb.home" d="Home" />
            </Link>
            <span>›</span>
            <Link to={`/${lang}/platform/performance-management/`} className="hover:text-primary transition-colors">
              <T page={P} k="breadcrumb.parent" d="Performance Management" />
            </Link>
            <span>›</span>
            <span style={{ color: "#2A2334" }}>
              <T page={P} k="breadcrumb.current" d="Carrièrepaden" />
            </span>
          </div>

          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center mb-12">
            <div className="animate-fade-in">
              <p className="text-lead text-muted-foreground mb-4">
                <T page={P} k="hero.subtitle" d="Carrièrepaden in de job matrix" />
              </p>
              <h1 className="mb-6" style={{ color: '#2A2334' }}>
                <T page={P} k="hero.title.lead" d="Laat zien wat de volgende stap is" />{' '}
                <T page={P} k="hero.title.accent" d="en wat ervoor nodig is" as="span" />
              </h1>
              <THtml page={P} k="hero.body" d="Maak voor iedereen inzichtelijk wat er nodig is om te groeien, in de huidige rol en in de volgende. Onze AI-coach helpt de weg daarnaartoe uit te stippelen." as="div" className="text-lead text-muted-foreground mb-8 max-w-xl" />
              <div className="flex flex-col sm:flex-row gap-4">
                <Button size="lg" className="text-sm px-6 py-5 rounded-full bg-[#9055EE] hover:bg-[#753DCE]" onClick={() => window.open('https://app.learned.io/register', '_blank')}>
                  <T page={P} k="hero.tryFree" d="Probeer gratis" />
                </Button>
                <Button variant="outline" size="lg" className="text-sm px-6 py-5 rounded-full bg-white hover:bg-gray-50" onClick={() => document.getElementById('schedule-demo')?.scrollIntoView({ behavior: 'smooth' })}>
                  <Calendar className="mr-2 h-4 w-4" style={{ color: '#9055EE' }} />
                  <T page={P} k="hero.planDemo" d="Plan een demo" />
                </Button>
              </div>
            </div>

            <div className="animate-fade-in relative" style={{ animationDelay: '200ms' }}>
              <EditableImage
                page={P}
                section="hero"
                imageKey="hero-image"
                src={carrierepadenHero}
                alt="Twee collega's bespreken samen het carrièrepad achter een laptop"
                className="w-full h-[450px] object-cover object-[62%_center] rounded-2xl"
                loading="eager"
              />
              <div className="absolute -right-8 -top-5 bg-white rounded-full shadow-xl ring-[8px] ring-white/80 px-4 py-2.5 transform rotate-3 hover:rotate-0 transition-transform duration-300 hidden lg:flex items-center gap-2 opacity-0 animate-fade-float-in" style={{ animationDelay: '1100ms', animationFillMode: 'forwards' }}>
                <Map className="w-4 h-4 text-[#9055EE]" />
                <T page={P} k="hero.cards.pathVisible" d="Volgende stap zichtbaar" as="span" className="text-sm font-semibold text-gray-700" />
              </div>
              <div className="absolute -right-6 bottom-24 bg-white rounded-full shadow-xl ring-[8px] ring-white/80 px-4 py-2.5 transform -rotate-2 hover:rotate-0 transition-transform duration-300 hidden lg:flex items-center gap-2 opacity-0 animate-fade-float-in" style={{ animationDelay: '1400ms', animationFillMode: 'forwards' }}>
                <CheckCircle2 className="w-4 h-4 text-green-500" />
                <T page={P} k="hero.cards.skillgap" d="Skillgap concreet gemaakt" as="span" className="text-sm font-semibold text-gray-700" />
              </div>
              <div className="absolute -left-6 bottom-10 bg-white rounded-full shadow-xl ring-[8px] ring-white/80 px-4 py-2.5 transform -rotate-2 hover:rotate-0 transition-transform duration-300 hidden lg:flex items-center gap-2 opacity-0 animate-fade-float-in" style={{ animationDelay: '1700ms', animationFillMode: 'forwards' }}>
                <Sparkles className="w-4 h-4 text-[#FB9A24]" />
                <T page={P} k="hero.cards.aiCoach" d="AI-coach stippelt de route uit" as="span" className="text-sm font-semibold text-gray-700" />
              </div>
            </div>
          </div>
          <ClientLogos />
        </PageContainer>
      </section>

      {/* Challenge Block */}
      <section
        className="overflow-x-hidden px-4 py-20 sm:px-6"
        style={{ background: "linear-gradient(135deg, #2A2334 0%, #4A3660 50%, #2A2334 100%)" }}
      >
        <div className="mx-auto w-full max-w-5xl">
          <div className="grid items-center gap-12 lg:grid-cols-2 lg:gap-16">
            <div className="mx-auto flex w-full max-w-sm flex-col items-center gap-3 sm:max-w-none sm:flex-row sm:flex-wrap sm:justify-center sm:gap-4">
              {[
                { icon: Map, k: "challenges.pain1", d: "Geen zicht op de volgende stap" },
                { icon: AlertTriangle, k: "challenges.pain2", d: "Functieprofielen die niemand inziet" },
                { icon: Users, k: "challenges.pain3", d: "Managers missen het overzicht" },
                { icon: TrendingDown, k: "challenges.pain4", d: "Talent vertrekt naar buiten" },
              ].map((point, i) => {
                const Icon = point.icon;
                return (
                  <div
                    key={i}
                    className="flex w-full items-center justify-center gap-3 rounded-full bg-white/10 px-4 py-3 backdrop-blur-sm sm:w-auto sm:px-5"
                  >
                    <Icon className="w-4 h-4 text-white/60 shrink-0" />
                    <T page={P} k={point.k} d={point.d} as="span" className="text-center text-sm leading-snug text-white/80" />
                  </div>
                );
              })}
            </div>

            <div>
              <T page={P} k="challenges.eyebrow" d="De uitdaging" as="p" className="text-xs font-semibold uppercase tracking-wider text-white/60 mb-2" />
              <h2 className="feature-section-heading text-white mb-6">
                <T page={P} k="challenges.title" d="Het recept voor vertrek: ontbrekende groeipaden" />
              </h2>
              <THtml
                page={P}
                k="challenges.body1"
                d="Veelal wordt bijgestuurd op hoe een medewerker succesvol kan zijn in zijn huidige rol, maar zodra de vraag verschuift naar &quot;en wat daarna?&quot;, blijft het stil. Die informatie zit verspreid over functieprofielen die niemand inziet, en het hoofd van een manager die het ook niet meer scherp heeft. Het gevolg: wie geen groeipad ziet, gaat er buiten de organisatie naar op zoek."
                as="p"
                className="text-sm text-white/80 leading-relaxed"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="pt-[60px] pb-20 px-6">
        <PageContainer>
          <div className="space-y-16">
            {features.map((feature, index) => {
              const isReversed = index % 2 === 1;
              return (
                <div
                  key={index}
                  className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center animate-fade-in"
                  style={{ animationDelay: `${index * 100}ms` }}>

                  <div className={`${isReversed ? 'lg:order-2' : 'lg:order-1'}`}>
                    <h2 className="feature-section-heading text-foreground">
                      <T page={P} k={feature.titleKey} d={feature.titleDefault} />
                    </h2>
                    <THtml page={P} k={feature.bodyKey} d={feature.bodyDefault} as="p" className="text-lead text-muted-foreground" />

                    {(feature.cta || feature.demoCta) &&
                    <Button asChild className="mt-6 rounded-full bg-[#9055EE] hover:bg-[#753DCE] text-white">
                        <Link to={demoPath}>
                          <T page={P} k="features.demoCta" d="Plan een demo" />
                          <ArrowRight className="ml-2 h-4 w-4" />
                        </Link>
                      </Button>
                    }
                  </div>
                  <div className={`${isReversed ? 'lg:order-1' : 'lg:order-2'}`}>
                    {(feature as any).noFrame ? (
                      <EditableImage
                        page={P}
                        section="features"
                        imageKey={feature.imageKey}
                        src={(feature as any).image}
                        alt={(feature as any).imageAlt}
                        className="w-full"
                      />
                    ) : (
                      /* TODO: echte productafbeelding toevoegen (Visuals: section features) */
                      <FeatureImageFrame color={feature.color} />
                    )}
                  </div>
                </div>);

            })}
          </div>
        </PageContainer>
      </section>


      <PartnerForChange />

      <ScheduleDemo />
    </>);

};

export default Carrierepaden;
